import React, { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  AppBar,
  Toolbar,
  IconButton,
  Stack,
  TextField,
  Paper,
  Divider,
  Alert,
  alpha
} from '@mui/material';
import {
  Analytics as AnalyticsIcon,
  ArrowBack as ArrowBackIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  LocationOn as LocationIcon,
  Support as SupportIcon,
  Business as BusinessIcon,
  Send as SendIcon,
  AccessTime as TimeIcon
} from '@mui/icons-material';

import civiqLogo from '../img/civiq.jpg';

const Contact = ({ onNavigate }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [submitted, setSubmitted] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    // Simulate form submission
    setSubmitted(true);
    setTimeout(() => {
      setSubmitted(false);
      setFormData({ name: '', email: '', subject: '', message: '' });
    }, 3000);
  };

  const contactMethods = [
    {
      icon: <EmailIcon />,
      title: 'Email Us',
      description: 'Send us an email and we\'ll respond within 24 hours',
      contact: 'satvik140802@gmail.com',
      action: 'mailto:satvik140802@gmail.com',
      color: '#1976d2'
    },
    {
      icon: <PhoneIcon />,
      title: 'Call Us',
      description: 'Speak directly with our team during business hours',
      contact: '+91 90984 12XXX',
      action: 'tel:+919876543210',
      color: '#388e3c'
    },
    {
      icon: <SupportIcon />,
      title: 'Live Chat',
      description: 'Get instant support through our live chat system',
      contact: 'Available 9 AM - 6 PM IST',
      action: '#',
      color: '#f57c00'
    },
    {
      icon: <LocationIcon />,
      title: 'Visit Us',
      description: 'Meet us in person at our office in Bangalore',
      contact: 'Bangalore, Karnataka, India',
      action: '#',
      color: '#7b1fa2'
    }
  ];

  const departments = [
    {
      title: 'Sales & Partnerships',
      email: 'satvik140802@gmail.com',
      description: 'Pricing, partnerships, and enterprise solutions'
    },
    {
      title: 'Technical Support',
      email: 'satvik140802@gmail.com',
      description: 'API issues, technical questions, and bug reports'
    },
    {
      title: 'Media & Press',
      email: 'satvik140802@gmail.com',
      description: 'Press inquiries, interviews, and media requests'
    },
    {
      title: 'General Inquiries',
      email: 'satvik140802@gmail.com',
      description: 'General questions and feedback'
    }
  ];

  return (
    <Box sx={{ backgroundColor: '#f8fafc', minHeight: '100vh' }}>
      {/* Header */}
      <AppBar position="static" sx={{ background: 'linear-gradient(135deg, #1976d2 0%, #7b1fa2 100%)' }}>
        <Toolbar>
          <IconButton 
            edge="start" 
            color="inherit" 
            onClick={() => onNavigate('landing')}
            sx={{ mr: 2 }}
          >
            <ArrowBackIcon />
          </IconButton>
          <Box sx={{ flexGrow: 1, display: 'flex', alignItems: 'center' }}>
            <Box
              component="img"
              src={civiqLogo}
              alt="CivIQ Logo"
              sx={{
                width: 32,
                height: 32,
                borderRadius: 1,
                mr: 1,
                border: '1px solid rgba(255,255,255,0.3)'
              }}
            />
            {/* <AnalyticsIcon sx={{ mr: 1 }} /> */}
            <Typography variant="h6" fontWeight="bold">
              Contact CivIQ
            </Typography>
          </Box>
        </Toolbar>
      </AppBar>

      {/* Hero Section */}
      <Box sx={{ py: 8, background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: 'white' }}>
        <Container maxWidth="lg">
          <Box textAlign="center">
            <Typography variant="h2" fontWeight="bold" gutterBottom>
              Get in Touch
            </Typography>
            <Typography variant="h2" fontWeight="bold" gutterBottom sx={{ 
              background: 'linear-gradient(45deg, #ffd700, #ffeb3b)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent'
            }}>
              With Our Team
            </Typography>
            <Typography variant="h6" sx={{ maxWidth: 600, mx: 'auto', mt: 3, opacity: 0.9 }}>
              Have questions about CivIQ? Need technical support? Want to explore partnership opportunities? 
              We're here to help.
            </Typography>
          </Box>
        </Container>
      </Box>

      {/* Contact Methods */}
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Typography variant="h3" fontWeight="bold" textAlign="center" gutterBottom>
          How to Reach Us
        </Typography>
        <Typography variant="h6" textAlign="center" color="text.secondary" sx={{ mb: 6 }}>
          Choose the method that works best for you
        </Typography>
        
        <Grid container spacing={4}>
          {contactMethods.map((method, index) => (
            <Grid item xs={12} sm={6} md={3} key={index}>
              <Card sx={{ 
                textAlign: 'center',
                p: 3,
                height: '100%',
                transition: 'all 0.3s ease',
                cursor: 'pointer',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: `0 12px 24px ${alpha(method.color, 0.15)}`
                }
              }}>
                <Box sx={{
                  width: 60,
                  height: 60,
                  borderRadius: 2,
                  background: alpha(method.color, 0.1),
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mx: 'auto',
                  mb: 2
                }}>
                  {React.cloneElement(method.icon, { 
                    sx: { color: method.color, fontSize: 32 } 
                  })}
                </Box>
                <Typography variant="h6" fontWeight="bold" gutterBottom>
                  {method.title}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  {method.description}
                </Typography>
                <Typography variant="subtitle1" fontWeight="600" color={method.color}>
                  {method.contact}
                </Typography>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* Contact Form & Departments */}
      <Box sx={{ py: 8, backgroundColor: 'white' }}>
        <Container maxWidth="lg">
          <Grid container spacing={6}>
            {/* Contact Form */}
            <Grid item xs={12} md={8}>
              <Paper sx={{ p: 4 }}>
                <Typography variant="h4" fontWeight="bold" gutterBottom>
                  Send us a Message
                </Typography>
                <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
                  Fill out the form below and we'll get back to you as soon as possible.
                </Typography>

                {submitted && (
                  <Alert severity="success" sx={{ mb: 3 }}>
                    Thank you for your message! We'll get back to you within 24 hours.
                  </Alert>
                )}

                <form onSubmit={handleSubmit}>
                  <Grid container spacing={3}>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Your Name"
                        name="name"
                        value={formData.name}
                        onChange={handleInputChange}
                        required
                        variant="outlined"
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Email Address"
                        name="email"
                        type="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        required
                        variant="outlined"
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Subject"
                        name="subject"
                        value={formData.subject}
                        onChange={handleInputChange}
                        required
                        variant="outlined"
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Message"
                        name="message"
                        multiline
                        rows={6}
                        value={formData.message}
                        onChange={handleInputChange}
                        required
                        variant="outlined"
                        placeholder="Tell us how we can help you..."
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <Button
                        type="submit"
                        variant="contained"
                        size="large"
                        startIcon={<SendIcon />}
                        sx={{
                          px: 4,
                          py: 1.5,
                          fontWeight: 600
                        }}
                      >
                        Send Message
                      </Button>
                    </Grid>
                  </Grid>
                </form>
              </Paper>
            </Grid>

            {/* Department Contacts */}
            <Grid item xs={12} md={4}>
              <Stack spacing={3}>
                <Box>
                  <Typography variant="h5" fontWeight="bold" gutterBottom>
                    Department Contacts
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Reach out to the specific team for faster assistance
                  </Typography>
                </Box>

                {departments.map((dept, index) => (
                  <Paper key={index} sx={{ p: 3, border: '1px solid #e0e0e0' }}>
                    <Typography variant="h6" fontWeight="600" gutterBottom>
                      {dept.title}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      {dept.description}
                    </Typography>
                    <Typography variant="subtitle2" color="primary.main" fontWeight="600">
                      {dept.email}
                    </Typography>
                  </Paper>
                ))}

                <Paper sx={{ p: 3, backgroundColor: '#f8fafc', border: '1px solid #e0e0e0' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <TimeIcon sx={{ color: 'primary.main', mr: 1 }} />
                    <Typography variant="h6" fontWeight="600">
                      Business Hours
                    </Typography>
                  </Box>
                  <Typography variant="body2" color="text.secondary">
                    Monday - Friday: 9:00 AM - 6:00 PM IST<br />
                    Saturday: 10:00 AM - 2:00 PM IST<br />
                    Sunday: Closed
                  </Typography>
                </Paper>
              </Stack>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Office Information */}
      <Box sx={{ py: 8, backgroundColor: '#f8fafc' }}>
        <Container maxWidth="lg">
          <Grid container spacing={6} alignItems="center">
            <Grid item xs={12} md={6}>
              <Typography variant="h3" fontWeight="bold" gutterBottom>
                Visit Our Office
              </Typography>
              <Typography variant="h6" color="text.secondary" sx={{ mb: 3 }}>
                We'd love to meet you in person at our headquarters in Bangalore
              </Typography>
              
              <Stack spacing={2}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <LocationIcon sx={{ color: 'primary.main', mr: 2 }} />
                  <Box>
                    <Typography variant="subtitle1" fontWeight="600">
                      JCR Layout, Panathur
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      4th Floor, Palika Place<br />
                      Bangalore, Karnataka, India - 110001<br />
                      India
                    </Typography>
                  </Box>
                </Box>
                
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <PhoneIcon sx={{ color: 'primary.main', mr: 2 }} />
                  <Box>
                    <Typography variant="subtitle1" fontWeight="600">
                      +91 90984 12XXX
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Main Office Line
                    </Typography>
                  </Box>
                </Box>
                
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <EmailIcon sx={{ color: 'primary.main', mr: 2 }} />
                  <Box>
                    <Typography variant="subtitle1" fontWeight="600">
                      satvik140802@gmail.com
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      General Inquiries
                    </Typography>
                  </Box>
                </Box>
              </Stack>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <Paper sx={{ 
                p: 4, 
                textAlign: 'center',
                background: 'linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%)'
              }}>
                <BusinessIcon sx={{ fontSize: 80, color: 'primary.main', mb: 2 }} />
                <Typography variant="h5" fontWeight="bold" gutterBottom>
                  Schedule a Meeting
                </Typography>
                <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                  Want to discuss a partnership or enterprise solution? 
                  Let's schedule a meeting to explore how we can work together.
                </Typography>
                <Button 
                  variant="contained" 
                  size="large"
                  sx={{ px: 4, py: 1.5, fontWeight: 600 }}
                >
                  Book a Meeting
                </Button>
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* FAQ Section */}
      <Box sx={{ py: 8, backgroundColor: 'white' }}>
        <Container maxWidth="md">
          <Typography variant="h3" fontWeight="bold" textAlign="center" gutterBottom>
            Quick Answers
          </Typography>
          <Typography variant="h6" textAlign="center" color="text.secondary" sx={{ mb: 6 }}>
            Common questions and their answers
          </Typography>
          
          <Stack spacing={3}>
            {[
              {
                question: "How quickly do you respond to inquiries?",
                answer: "We typically respond to all inquiries within 24 hours during business days. For urgent technical issues, we aim to respond within 4 hours."
              },
              {
                question: "Do you offer phone support?",
                answer: "Yes, phone support is available for Professional and Enterprise plan customers during business hours (9 AM - 6 PM IST)."
              },
              {
                question: "Can I schedule a product demo?",
                answer: "Absolutely! We offer personalized product demos for potential customers. Contact our sales team to schedule one."
              },
              {
                question: "Do you provide training for new users?",
                answer: "Yes, we offer comprehensive onboarding and training sessions for Enterprise customers and can arrange custom training for teams."
              }
            ].map((faq, index) => (
              <Paper key={index} sx={{ p: 3, border: '1px solid #e0e0e0' }}>
                <Typography variant="h6" fontWeight="600" gutterBottom>
                  {faq.question}
                </Typography>
                <Typography variant="body1" color="text.secondary">
                  {faq.answer}
                </Typography>
              </Paper>
            ))}
          </Stack>
        </Container>
      </Box>

      {/* CTA Section */}
      <Box sx={{ py: 8, background: 'linear-gradient(135deg, #1976d2 0%, #7b1fa2 100%)', color: 'white' }}>
        <Container maxWidth="md">
          <Box textAlign="center">
            <Typography variant="h3" fontWeight="bold" gutterBottom>
              Ready to Get Started?
            </Typography>
            <Typography variant="h6" sx={{ mb: 4, opacity: 0.9 }}>
              Don't see what you're looking for? Reach out and we'll help you find the right solution.
            </Typography>
            <Stack direction="row" spacing={2} justifyContent="center">
              <Button 
                variant="contained" 
                size="large"
                onClick={() => onNavigate('login')}
                sx={{
                  backgroundColor: 'white',
                  color: 'primary.main',
                  px: 4,
                  py: 1.5,
                  fontWeight: 600,
                  '&:hover': {
                    backgroundColor: alpha('#fff', 0.9)
                  }
                }}
              >
                Start Free Trial
              </Button>
              <Button 
                variant="outlined" 
                size="large"
                onClick={() => onNavigate('pricing')}
                sx={{
                  borderColor: 'white',
                  color: 'white',
                  px: 4,
                  py: 1.5,
                  fontWeight: 600,
                  '&:hover': {
                    borderColor: 'white',
                    backgroundColor: alpha('#fff', 0.1)
                  }
                }}
              >
                View Pricing
              </Button>
            </Stack>
          </Box>
        </Container>
      </Box>
      {/* Footer */}
            <Box sx={{ py: 6, backgroundColor: '#263238', color: 'white' }}>
              <Container maxWidth="lg">
                <Grid container spacing={4}>
                  <Grid item xs={12} md={6}>
                    <Box display="flex" alignItems="center" mb={2}>
                      <Box 
                        sx={{ 
                          width: 40, 
                          height: 40, 
                          // background: 'linear-gradient(135deg, #1976d2 0%, #7b1fa2 100%)',
                          borderRadius: 2,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          mr: 2
                        }}
                      >
                        {/* <AnalyticsIcon sx={{ color: 'white', fontSize: 24 }} /> */}
                        <Box
                                      component="img"
                                      src={civiqLogo}
                                      alt="CivIQ Logo"
                                      sx={{
                                        width: 40,
                                        height: 40,
                                        borderRadius: 1,
                                        mr: 1,
                                        border: '1px solid rgba(255,255,255,0.3)'
                                      }}
                                    />
                      </Box>
                      <Typography variant="h5" fontWeight="bold">
                        CivIQ
                      </Typography>
                    </Box>
                    <Typography variant="body2" color="rgba(255,255,255,0.7)" sx={{ maxWidth: 400 }}>
                      AI-powered political analytics platform for India's elections. Making complex political data accessible and actionable.
                    </Typography>
                  </Grid>
                  
                  <Grid item xs={12} md={6}>
                    <Grid container spacing={2}>
                      <Grid item xs={6}>
                        <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                          Platform
                        </Typography>
                        <Box>
                          <Button 
                            color="inherit" 
                            size="small" 
                            onClick={() => onNavigate('features')}
                            sx={{ justifyContent: 'flex-start', p: 0.5 }}
                          >
                            Features
                          </Button>
                        </Box>
                        <Box>
                          <Button 
                            color="inherit" 
                            size="small" 
                            onClick={() => onNavigate('pricing')}
                            sx={{ justifyContent: 'flex-start', p: 0.5 }}
                          >
                            Pricing
                          </Button>
                        </Box>
                        <Box>
                          <Button 
                            color="inherit" 
                            size="small" 
                            onClick={() => onNavigate('api')}
                            sx={{ justifyContent: 'flex-start', p: 0.5 }}
                          >
                            API
                          </Button>
                        </Box>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                          Company
                        </Typography>
                        <Box>
                          <Button 
                            color="inherit" 
                            size="small" 
                            onClick={() => onNavigate('about')}
                            sx={{ justifyContent: 'flex-start', p: 0.5 }}
                          >
                            About
                          </Button>
                        </Box>
                        <Box>
                          <Button 
                            color="inherit" 
                            size="small" 
                            onClick={() => onNavigate('contact')}
                            sx={{ justifyContent: 'flex-start', p: 0.5 }}
                          >
                            Contact
                          </Button>
                        </Box>
                        <Box>
                          <Button 
                            color="inherit" 
                            size="small" 
                            onClick={() => onNavigate('privacy')}
                            sx={{ justifyContent: 'flex-start', p: 0.5 }}
                          >
                            Privacy Policy
                          </Button>
                        </Box>
                      </Grid>
                    </Grid>
                  </Grid>
                </Grid>
                
                <Divider sx={{ my: 4, borderColor: 'rgba(255,255,255,0.2)' }} />
                
                <Box textAlign="center">
                  <Typography variant="body2" color="rgba(255,255,255,0.7)">
                    © 2025 CivIQ. All rights reserved.
                  </Typography>
                </Box>
              </Container>
            </Box>
    </Box>
  );
};

export default Contact;
